/* eslint-disable no-unused-vars */
import { forwardRef, useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSignal } from '@preact/signals-react';
import { Buttons, Label, Bid, Message } from 'components';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { campaignsManageAPI } from 'services/campaignsManage.service';
import { isAdminSelector } from 'store/selectors';
import { showPolipop, isBidErrorShown, isInvalidBid, trimDecimal } from 'utils';
import { ManageCampaignsContext } from 'contexts';
import { ErrorIcon } from 'assets/icons';

import * as S from './DailyBudgetTooltip.styled';

/**
 * UA Budget Validation - Build error message for an active backend rule.
 * Mirrors the messages used in the campaign edit form for consistency.
 * Only invoked when the rule is active ("CPI" | "Placements" | "Broad").
 * @see JIRA: UA Campaign Budget Validation
 */
const getBudgetRuleErrorMessage = (rule, minimum) => {
  const formattedMinimum = Number.isInteger(minimum) ? minimum.toString() : minimum.toFixed(2);

  switch ((rule || '').toLowerCase()) {
    case 'cpi':
      return `Budget must be at least 10x the Target CPI ($${formattedMinimum}).`;
    case 'broad':
      return `Broad campaigns (All Placements) require a minimum budget of $100.`;
    case 'placements':
    default:
      return `Based on your targeting (Placements x Countries), the minimum budget is $${formattedMinimum}.`;
  }
};

/**
 * Inline budget edit tooltip for Manage Campaigns grid.
 *
 * UA Budget Validation (budget-only edit):
 * - Sends only campaignId; backend uses saved campaign data (case 3).
 * - When the entered budget is below the returned minimum, the Ok button is
 *   disabled and a red inline error message is shown.
 *
 * @see JIRA: UA Campaign Budget Validation
 */
export const DailyBudgetTooltip = forwardRef((props, ref) => {
  const isAdmin = useSelector(isAdminSelector);
  const budgetValue$ = useSignal(props.budgetValue);
  const changesFlag = useRef(true);
  const errorText$ = useSignal('');
  const [updateDailyBudget, result] = saveCampaignServiceAPI.useUpdateDailyBudgetMutation();

  const [getMinBudget, { data: budgetValidation }] =
    campaignsManageAPI.useGetMinimumRequiredBudgetMutation();

  const isCPI = props.data.bid_type === 3;

  const MAX_DAILY_BUDGET = isCPI ? window.cpi_max_daily_budget : window.max_daily_budget;
  const MIN_DAILY_BUDGET = isAdmin ? window.min_daily_budget_admin : window.min_daily_budget;

  const dateForRequest = useSelector((state) => state.manageCampaigns.selectedDateRange);
  const { updateTableData } = useContext(ManageCampaignsContext);

  useEffect(() => {
    if (props.data?.campaignId) {
      getMinBudget(props.data.campaignId);
    }
  }, [props.data?.campaignId]);

  useEffect(() => {
    if (result.isSuccess || result.isError) {
      showPolipop(result);
    }
    if (result.isSuccess) {
      updateTableData({
        newValue: +budgetValue$.value,
        fieldName: 'budget',
        campId: props.data.campaignId,
      });
      props.isOpen$.value = false;
    }
  }, [result]);

  // Legacy campaigns: backend returns applicableRule = 'LegacyMinimum' (deterministic);
  // 'None' is also treated as no-rule. Both fall back to the original error messages.
  const minimumBudget = budgetValidation?.minimumBudget || 0;
  const applicableRule = budgetValidation?.applicableRule || 'None';
  const isRuleActive =
    minimumBudget > 0 && applicableRule !== 'None' && applicableRule !== 'LegacyMinimum';
  const budgetRuleError = isRuleActive && Number(budgetValue$.value || 0) < minimumBudget;

  const isErrorShown =
    isBidErrorShown(budgetValue$.value, MIN_DAILY_BUDGET, MAX_DAILY_BUDGET) || budgetRuleError;
  const isDisabled =
    isInvalidBid(budgetValue$.value, props.budgetValue, MIN_DAILY_BUDGET, MAX_DAILY_BUDGET) ||
    budgetRuleError;

  const computeErrorMessage = (val) => {
    if (isRuleActive && Number(val || 0) < minimumBudget) {
      return getBudgetRuleErrorMessage(applicableRule, minimumBudget);
    }
    if (val === undefined || val === null || val === '') return '';
    if (val < MIN_DAILY_BUDGET || val === '.') return `Minimum daily budget is ${MIN_DAILY_BUDGET}`;
    if (val > MAX_DAILY_BUDGET) return `Maximum daily budget is $${MAX_DAILY_BUDGET}`;
    return '';
  };

  // Recompute the error text whenever the validation result changes (e.g. arrives from server)
  useEffect(() => {
    errorText$.value = computeErrorMessage(budgetValue$.value);
  }, [minimumBudget, applicableRule]);

  const handleBudgetChange = (e) => {
    changesFlag.current = false;
    errorText$.value = computeErrorMessage(e.target.value);
    budgetValue$.value = trimDecimal(e.target.value);
  };

  const handleBlur = (e) => {
    const currentBudget = e.target.value;
    if (currentBudget.startsWith('.')) {
      budgetValue$.value = trimDecimal('0' + currentBudget);
    }
  };

  const handleSaveBudget = () => {
    if (isDisabled) return;

    const budgetToSave = budgetValue$.value.toString().startsWith('.')
      ? trimDecimal('0' + budgetValue$.value)
      : budgetValue$.value;

    if (isInvalidBid(budgetValue$.value, props.budgetValue, MIN_DAILY_BUDGET, MAX_DAILY_BUDGET))
      return;

    const budgetReq = {
      campaignId: props.data.campaignId,
      dailyBudget: budgetToSave,
    };
    updateDailyBudget(budgetReq);
  };

  return (
    <div ref={ref} style={{ padding: '16px', width: '280px' }}>
      <S.BudgetInput>
        <Label
          text='Daily Budget:'
          variant='label'
          style={{ marginBottom: '4px', lineHeight: '40px' }}
        />
        <Bid.Big
          withError={false}
          value={budgetValue$.value}
          onBlur={handleBlur}
          onChange={handleBudgetChange}
        />
      </S.BudgetInput>
      <S.ErrorWrap style={{ opacity: isErrorShown && errorText$.value ? '1' : '0' }}>
        <ErrorIcon style={{ flexShrink: 0 }} />
        <span>{errorText$.value}</span>
      </S.ErrorWrap>

      <S.ButtonsWrap>
        <Buttons.SecondaryNormal
          id='close-changeBid-tooltip'
          isDisabled={result.isLoading}
          style={{ width: '100%' }}
          onClick={() => (props.isOpen$.value = false)}
        >
          Cancel
        </Buttons.SecondaryNormal>
        <Buttons.PrimaryNormal
          id='save--changeBid-tooltip'
          style={{ width: '100%' }}
          onClick={handleSaveBudget}
          isDisabled={isErrorShown || isDisabled || result.isLoading || changesFlag.current}
        >
          Ok
        </Buttons.PrimaryNormal>
      </S.ButtonsWrap>
    </div>
  );
});

DailyBudgetTooltip.displayName = 'DailyBudgetTooltip';
